import { useEffect, useMemo, useRef } from "react"

import { type QueryClient, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"
import type { BatteryArbitrageDto } from "~/api/dto/battery-arbitrage.dto"
import type {
  BatteryProposalDto,
  BatteryProposalFinancialSummaryResponseDto,
} from "~/api/dto/battery-proposal.dto"
import {
  ARBITRAGE_POLL_MS,
  isBatteryArbitrageInFlight,
} from "~/api/hooks/useBatteryProposalArbitrage"
import useCalculateBatteryProposalPricing from "~/api/hooks/useCalculateBatteryProposalPricing"
import useRetryBatteryProposalArbitrage from "~/api/hooks/useRetryBatteryProposalArbitrage"
import { batteryProposalService } from "~/api/services/battery-proposal.service"

import {
  BATTERY_LIGHTREACH_PAYMENT_UPDATE_MESSAGE,
  BATTERY_PAYMENT_UPDATE_MESSAGE,
  BATTERY_UPDATES_SPEC,
  batteryPaymentFix,
  batterySavingsInFlight,
  BatterySavingsStop,
  batterySavingsStop,
  describeBatteryUpdateError,
  isBatteryUpdateNeeded,
  SAVINGS_CHECK_ERROR_MESSAGE,
  SAVINGS_STILL_CALCULATING_MESSAGE,
  type BatteryUpdateKey,
  type BatteryUpdateReads,
} from "./battery-updates"
import {
  type UpdatesRunner,
  type UpdatesRunnerConfig,
  useUpdatesRunner,
} from "../ProposalPresentation/updates/use-updates-runner"

/**
 * How long the panel waits on a queued savings calculation. The queue picks
 * one up within seconds; past this the row says so until the run moves on,
 * and the Setup rail carries it from there.
 */
export const BATTERY_SAVINGS_WAIT_MS = 5 * 60_000

export type BatteryUpdatesRunner = UpdatesRunner<BatteryUpdateKey>

/**
 * How the panel left a savings run: the Proposal read shows it ended, or
 * still reads it in the queue (the panel stopped waiting, or the read is
 * behind).
 */
export type BatterySavingsOutcome = `ended` | `queued`

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Starts a savings calculation and waits for the queue to finish it, reading
 * the lifecycle as the Battery Savings step does. A run that ends failed,
 * unavailable or out of date stops the panel with its reason, once the
 * Proposal read says so too.
 */
export const calculateBatterySavings = async ({
  queryClient,
  proposalId,
  start,
  waitMs = BATTERY_SAVINGS_WAIT_MS,
  abandoned = () => false,
  onStarted = () => undefined,
  onEnded = () => undefined,
}: {
  queryClient: QueryClient
  proposalId: string
  /** The retry command; it publishes its answer to the arbitrage read. */
  start: () => Promise<BatteryArbitrageDto>
  waitMs?: number
  /** The page left: stop reading, and leave the run to the queue. */
  abandoned?: () => boolean
  /** The queue took the run: the panel follows it from here. */
  onStarted?: () => void
  /** The panel stopped waiting, however the run went. */
  onEnded?: (outcome: BatterySavingsOutcome) => void
}): Promise<void> => {
  // The Proposal read as the cache holds it, which renders a tick later.
  const outcome = (): BatterySavingsOutcome =>
    batterySavingsInFlight({
      savings: queryClient.getQueryData<BatteryProposalDto>(
        queryKeys.batteryProposal.byId(proposalId),
      )?.arbitrage,
    })
      ? `queued`
      : `ended`
  let arbitrage = await start()
  onStarted()
  const deadline = Date.now() + waitMs
  while (isBatteryArbitrageInFlight(arbitrage)) {
    if (Date.now() >= deadline) {
      onEnded(outcome())
      throw new BatterySavingsStop(SAVINGS_STILL_CALCULATING_MESSAGE)
    }
    await wait(ARBITRAGE_POLL_MS)
    if (abandoned()) return
    try {
      arbitrage = await queryClient.fetchQuery({
        queryKey: queryKeys.batteryProposal.arbitrage(proposalId),
        queryFn: () => batteryProposalService.getArbitrage(proposalId),
        staleTime: 0,
        retry: 2,
      })
    } catch {
      // The run started; only this check failed.
      onEnded(outcome())
      throw new BatterySavingsStop(SAVINGS_CHECK_ERROR_MESSAGE)
    }
  }
  const stop = batterySavingsStop(arbitrage, proposalId)
  if (stop) {
    // The runner reads again only after a step that succeeds, and the page
    // still holds the queued run's lifecycle.
    await queryClient.refetchQueries({
      queryKey: queryKeys.batteryProposal.byId(proposalId),
      exact: true,
    })
    onEnded(outcome())
    throw stop
  }
  onEnded(outcome())
}

/**
 * The Battery Updates panel's runner, on the existing commands: Refresh
 * Pricing, then Recalculate savings. A payment that stays stale on a current
 * price is quoted again on Loan Product, which the panel links to rather than
 * runs.
 * Savings wait for the background calculation, so their row shows it running
 * until the queue answers.
 */
export const useBatteryUpdatesRunner = ({
  proposalId,
  settled,
  priceSetByOffer,
  savingsEnabled,
  onSavingsStarted,
  onSavingsEnded,
}: {
  proposalId: string
  /** Nothing is out of date any more: fixed rows clear after a moment. */
  settled: boolean
  priceSetByOffer: boolean
  savingsEnabled: boolean
  /** The queue took a savings run the panel started. Stable across renders. */
  onSavingsStarted: () => void
  /** The panel stopped waiting on that run. Stable too. */
  onSavingsEnded: (outcome: BatterySavingsOutcome) => void
}): BatteryUpdatesRunner => {
  const queryClient = useQueryClient()
  const { mutateAsync: refreshPricing } = useCalculateBatteryProposalPricing()
  const { mutateAsync: retrySavings } = useRetryBatteryProposalArbitrage()
  const unmounted = useRef(false)
  useEffect(() => {
    unmounted.current = false
    return () => {
      unmounted.current = true
    }
  }, [])

  const config = useMemo((): UpdatesRunnerConfig<
    BatteryUpdateKey,
    BatteryUpdateReads
  > => {
    const summaryKey = queryKeys.batteryProposal.financialSummary(proposalId)
    // The Proposal read carries the savings lifecycle the page renders by.
    const proposalKey = queryKeys.batteryProposal.byId(proposalId)
    return {
      spec: BATTERY_UPDATES_SPEC,
      readKeys: {
        pricing: summaryKey,
        payment: summaryKey,
        savings: proposalKey,
      },
      readsNow: () => ({
        pricing:
          queryClient.getQueryData<BatteryProposalFinancialSummaryResponseDto>(
            summaryKey,
          ),
        savings:
          queryClient.getQueryData<BatteryProposalDto>(proposalKey)?.arbitrage,
        priceSetByOffer,
        savingsEnabled,
      }),
      isNeeded: isBatteryUpdateNeeded,
      unrunnable: (key) =>
        key === `payment`
          ? {
              message: priceSetByOffer
                ? BATTERY_LIGHTREACH_PAYMENT_UPDATE_MESSAGE
                : BATTERY_PAYMENT_UPDATE_MESSAGE,
              fix: batteryPaymentFix(proposalId, priceSetByOffer),
            }
          : null,
      steps: {
        pricing: () => refreshPricing({ proposalId }),
        // Never reached: `unrunnable` stops a run at the payment.
        payment: () => Promise.resolve(),
        savings: () =>
          calculateBatterySavings({
            queryClient,
            proposalId,
            start: () => retrySavings({ proposalId }),
            abandoned: () => unmounted.current,
            onStarted: onSavingsStarted,
            onEnded: onSavingsEnded,
          }),
      },
      describeFailure: (key, error) =>
        describeBatteryUpdateError({ key, error, proposalId }),
    }
  }, [
    onSavingsEnded,
    onSavingsStarted,
    priceSetByOffer,
    proposalId,
    queryClient,
    refreshPricing,
    retrySavings,
    savingsEnabled,
  ])

  return useUpdatesRunner({ config, settled })
}
